import { useState, useEffect, useCallback, useMemo } from 'react';
import { GoogleMap, useJsApiLoader, Marker } from '@react-google-maps/api';
import { MapPin } from 'lucide-react';
import { configApi } from '@shared/services/configApi';
import type { ClinicWithDistance } from '@features/clinics/types/clinic';
import '@features/clinics/components/ClinicMap.css';

type GroupKey = 'related' | 'houseAccounts' | 'labcorp' | 'nearby';

// Maps markerCategory values to GroupKey for visibility lookup
const CATEGORY_TO_GROUP: Record<string, GroupKey> = {
  related: 'related',
  houseAccount: 'houseAccounts',
  labcorp: 'labcorp',
  nearby: 'nearby',
};

interface ClientRelationshipMapProps {
  customerLocation: { latitude: number; longitude: number; name?: string };
  clinics: ClinicWithDistance[];
  selectedClinicId?: number;
  onClinicSelect: (clinicId: number) => void;
  mapStyle?: React.CSSProperties;
  markerCategories?: Map<number, 'related' | 'houseAccount' | 'nearby' | 'labcorp'>;
  /** When set, groups NOT in this set render at reduced opacity */
  fadedGroups?: Set<GroupKey>;
}

interface ClientRelationshipMapInnerProps extends ClientRelationshipMapProps {
  apiKey: string;
  fadedGroups?: Set<GroupKey>;
}

const defaultContainerStyle = {
  width: '100%',
  height: '400px',
  borderRadius: '8px',
};

// Cache the API key to avoid fetching it multiple times
let cachedApiKey: string | null = null;
let apiKeyPromise: Promise<string | null> | null = null;

async function getApiKey(): Promise<string | null> {
  if (cachedApiKey) return cachedApiKey;
  if (apiKeyPromise) return apiKeyPromise;

  apiKeyPromise = configApi.getGoogleMapsApiKey().then((key) => {
    cachedApiKey = key;
    if (!key) {
      apiKeyPromise = null;
    }
    return key;
  });

  return apiKeyPromise;
}

// Inner component that uses the Google Maps hook
function ClientRelationshipMapInner({
  customerLocation,
  clinics,
  selectedClinicId,
  onClinicSelect,
  apiKey,
  mapStyle,
  markerCategories,
  fadedGroups,
}: Readonly<ClientRelationshipMapInnerProps>) {
  const containerStyle = mapStyle || defaultContainerStyle;
  const [map, setMap] = useState<google.maps.Map | null>(null);

  // Clinics without coordinates (possible for related clinics fetched by id)
  // can't be plotted — they still appear in the grids, just not on the map.
  const mappableClinics = useMemo(
    () =>
      clinics.filter(
        (c): c is ClinicWithDistance & { latitude: number; longitude: number } =>
          c.latitude != null && c.longitude != null
      ),
    [clinics]
  );

  const { isLoaded, loadError } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: apiKey,
  });

  // Center map on customer location and fit bounds when clinics change
  useEffect(() => {
    if (map && mappableClinics.length > 0) {
      const bounds = new window.google.maps.LatLngBounds();

      // Add customer location to bounds
      bounds.extend({
        lat: customerLocation.latitude,
        lng: customerLocation.longitude,
      });

      // Add all clinic locations to bounds
      mappableClinics.forEach((clinic) => {
        bounds.extend({
          lat: clinic.latitude,
          lng: clinic.longitude,
        });
      });

      map.fitBounds(bounds);

      // Add padding around bounds
      const padding = { top: 50, right: 50, bottom: 50, left: 50 };
      map.fitBounds(bounds, padding);
    }
  }, [map, mappableClinics, customerLocation]);

  const onLoad = useCallback((map: google.maps.Map) => {
    setMap(map);
  }, []);

  const onUnmount = useCallback(() => {
    setMap(null);
  }, []);

  const getMarkerOpacity = (clinicId: number): number => {
    if (!fadedGroups || !markerCategories) return 1;
    const category = markerCategories.get(clinicId);
    if (!category) return 1;
    const group = CATEGORY_TO_GROUP[category];
    return fadedGroups.has(group) ? 1 : 0.25;
  };

  const getMarkerColor = (clinicId: number, isSelected: boolean): string => {
    if (!markerCategories) {
      // Backward compatible: no categories = all red (existing behavior)
      return isSelected ? '#dc2626' : '#ef4444';
    }
    const category = markerCategories.get(clinicId);
    if (isSelected) {
      switch (category) {
        case 'related': return '#15803d';      // dark green
        case 'houseAccount': return '#c2410c'; // dark orange
        case 'labcorp': return '#7e22ce';      // dark purple
        default: return '#dc2626';              // dark red
      }
    }
    switch (category) {
      case 'related': return '#22c55e';     // green
      case 'houseAccount': return '#f97316'; // orange
      case 'labcorp': return '#a855f7';      // purple
      default: return '#ef4444';             // red
    }
  };

  if (loadError) {
    return (
      <div className="clinic-map-placeholder">
        <MapPin size={32} className="map-placeholder-icon" />
        <p>Error loading map</p>
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div className="clinic-map-loading">
        <div className="loading-spinner-small"></div>
        <p>Loading map...</p>
      </div>
    );
  }

  return (
    <div className="clinic-map-container">
      <GoogleMap
        mapContainerStyle={containerStyle}
        center={{
          lat: customerLocation.latitude,
          lng: customerLocation.longitude,
        }}
        zoom={10}
        onLoad={onLoad}
        onUnmount={onUnmount}
        options={{
          disableDefaultUI: true,
          zoomControl: true,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: true,
        }}
      >
        {/* Customer location marker (blue) */}
        <Marker
          position={{
            lat: customerLocation.latitude,
            lng: customerLocation.longitude,
          }}
          title={customerLocation.name || 'Customer Location'}
          icon={{
            path: window.google.maps.SymbolPath.CIRCLE,
            scale: 10,
            fillColor: '#3b82f6',
            fillOpacity: 1,
            strokeColor: '#ffffff',
            strokeWeight: 2,
          }}
        />

        {/* Clinic markers (red, highlighted if selected) */}
        {mappableClinics.map((clinic) => (
          <Marker
            key={clinic.id}
            position={{
              lat: clinic.latitude,
              lng: clinic.longitude,
            }}
            title={clinic.distanceMiles != null ? `${clinic.name} (${clinic.distanceMiles.toFixed(1)} mi)` : clinic.name}
            onClick={() => onClinicSelect(clinic.id)}
            icon={{
              path: window.google.maps.SymbolPath.CIRCLE,
              scale: selectedClinicId === clinic.id ? 12 : 8,
              fillColor: getMarkerColor(clinic.id, selectedClinicId === clinic.id),
              fillOpacity: getMarkerOpacity(clinic.id),
              strokeColor: '#ffffff',
              strokeWeight: 2,
              strokeOpacity: getMarkerOpacity(clinic.id),
            }}
          />
        ))}
      </GoogleMap>
    </div>
  );
}

export default function ClientRelationshipMap(props: Readonly<ClientRelationshipMapProps>) {
  const [apiKey, setApiKey] = useState<string | null>(cachedApiKey);
  const [apiKeyLoading, setApiKeyLoading] = useState(!cachedApiKey);

  useEffect(() => {
    if (!cachedApiKey) {
      getApiKey().then((key) => {
        setApiKey(key);
        setApiKeyLoading(false);
      });
    }
  }, []);

  if (apiKeyLoading) {
    return (
      <div className="clinic-map-loading">
        <div className="loading-spinner-small"></div>
        <p>Loading map...</p>
      </div>
    );
  }

  if (!apiKey) {
    return (
      <div className="clinic-map-placeholder">
        <MapPin size={32} className="map-placeholder-icon" />
        <p>Map unavailable</p>
      </div>
    );
  }

  return <ClientRelationshipMapInner {...props} apiKey={apiKey} />;
}
